<template>
  <div>
    <!-- Poll Display Settings Section -->
    <div class="mb-6">
      <h4 class="mb-4 text-sm font-semibold uppercase text-slate-400">Poll Display Settings</h4>

      <div class="mb-4">
        <label class="tb-label mb-2 block" for="displaySelector">Poll Display</label>

        <select
          id="displaySelector"
          v-model="selectedDisplayId"
          class="tb-select"
          data-entity="producer-editor-pollDisplay"
          data-test="graphics-editor-pollDisplay"
        >
          <option
            v-for="display in availableDisplays"
            :key="display.displayId"
            :value="display.displayId"
          >
            {{ display.title }}
          </option>
        </select>
      </div>

      <template v-if="pollData && availableDisplays.length > 0">
        <div class="mb-4 flex flex-wrap gap-x-6 gap-y-3">
          <div class="min-w-[220px] flex-1">
            <label class="tb-label mb-2 block">Round</label>

            <select
              class="tb-select"
              v-model="currentRoundIndex"
              data-entity="producer-editor-round"
              data-test="graphics-editor-round"
            >
              <option
                v-for="(round, index) in rounds"
                :key="`round-${index}`"
                :value="index"
              >
                {{ round.title || `Round ${index + 1}` }}
              </option>
            </select>
          </div>

          <div class="min-w-[180px]">
            <label class="tb-label mb-2 block">Display State</label>
            <div class="flex flex-wrap">
              <label
                v-for="state in pollStates"
                :key="state"
                class="border border-white/10 bg-producer-surfaceAlt px-4 py-2 text-producer-text first:rounded-l-[6px] first:border-r-0 last:rounded-r-[6px] last:border-l-0"
                :class="{
                  'bg-white/25': currentDisplayState === state,
                }"
              >
                <input
                  v-model="currentDisplayState"
                  type="radio"
                  :value="state"
                  class="sr-only"
                />
                {{ stateLabels[state] || state }}
              </label>
            </div>
          </div>

          <div class="min-w-[220px]">
            <label class="tb-label mb-2 block">Question</label>
            <div class="flex items-center gap-2">
              <button
                class="tb-btn tb-btn-contained medium-em"
                :disabled="currentQuestionIndex <= 1"
                data-test="graphics-editor-pollDisplay-previousQuestion"
                @click="currentQuestionIndex--"
              >
                <span class="fas fa-backward" />
              </button>

              <input
                type="number"
                :min="1"
                :max="questions.length"
                v-model.number="currentQuestionIndex"
                class="tb-input flex-grow max-w-[80px] text-center"
                data-test="graphics-editor-pollDisplay-questionInput"
              />

              <button
                class="tb-btn tb-btn-contained medium-em"
                data-test="graphics-editor-pollDisplay-nextQuestion"
                :disabled="currentQuestionIndex >= questions.length"
                @click="currentQuestionIndex++"
              >
                <span class="fas fa-forward" />
              </button>
            </div>
          </div>
        </div>

        <template v-if="currentDisplayState === 'results'">
          <div class="tb-checkbox flex items-center gap-2">
            <input
              type="checkbox"
              id="animateResults"
              v-model="animateResults"
              data-test="graphics-editor-pollDisplay-animateResults"
            />

            <label for="animateResults">Manually show results</label>
          </div>

          <div
            v-if="animateResults"
            class="mt-4"
          >
            <label class="tb-label mb-2 block">
              Vote Animation Controls
            </label>

            <div class="flex flex-wrap gap-3">
              <action-key
                v-for="(action, index) in actions"
                :key="index"
                :action="action"
                :production_context="production_context"
                :data-test="`graphics-editor-pollDisplay-${action.hotkey.toLowerCase()}`"
              />
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { getPoll } from "@/js/services/polls";
import ActionKey from "../../../panel-editor/hotkeys/ActionKey.vue";

export default {
  name: "PollDisplaySettings",

  components: {
    ActionKey,
  },

  props: {
    element: {
      type: Object,
      required: true,
    },

    graphic_fields: {
      type: Object,
      required: true,
    },

    graphic: {
      type: Object,
      required: true,
    },

    production_context: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      pollData: null,
      isLoadingPoll: false,
      actions: [
        {
          hotkey: 'I',
          display: 'Animate In',
          description: 'Use this hotkey to animate results in',
          value: 'animate-results-in',
          type: 'poll',
        },
        {
          hotkey: 'O',
          display: 'Animate Out',
          description: 'Use this hotkey to animate results out',
          value: 'animate-results-out',
          type: 'poll',
        },
      ],
    };
  },

  computed: {
    timelineId() {
      return this.graphic.timeline_id;
    },

    elementFields() {
      return this.graphic_fields[this.element.id];
    },

    // Get poll ID from element or fields
    pollId() {
      return this.elementFields?.pollId ?? this.element.pollId;
    },

    // Get display ID from element or fields
    displayId() {
      return this.elementFields?.displayId ?? this.element.displayId;
    },

    // Available displays from the poll
    availableDisplays() {
      if (!this.pollData?.displays) {
        return [];
      }

      // Filter to only non-interactive displays (displayType !== 'poll')
      return this.pollData.displays.filter((d) => d.displayType !== "poll");
    },

    // Currently selected display object
    currentDisplay() {
      return this.availableDisplays.find(
        (d) => d.displayId === this.selectedDisplayId
      );
    },

    rounds() {
      return this.pollData?.rounds || [];
    },

    questions() {
      const roundIndex = this.currentRoundIndex;
      return this.rounds[roundIndex]?.questions || [];
    },

    // Poll states available based on display settings
    pollStates() {
      if (!this.currentDisplay?.settings) {
        return ["preVote", "results", "closed"];
      }

      const sortingArray = ["preVote", "results", "closed"];
      const { defaults, displaySettings, popUp, ...rest } =
        this.currentDisplay.settings;
      return Object.keys(rest).sort(
        (a, b) => sortingArray.indexOf(a) - sortingArray.indexOf(b)
      );
    },

    stateLabels() {
      if (this.currentDisplay?.displayType === "poll") {
        return {
          preVote: "Open",
          results: "Post Vote",
          closed: "Closed",
        };
      }

      return {
        preVote: "Open",
        results: "Results",
        closed: "Final",
      };
    },

    selectedDisplayId: {
      get() {
        // Check override first, then element default
        return this.elementFields?.displayId ?? this.element.displayId;
      },

      set(value) {
        const display = this.availableDisplays.find(
          (d) => d.displayId === value
        );

        if (!display) {
          return;
        }

        this.updateField("displayId", value);

        // Don't update pollId as it should remain the same for this poll's displays
      },
    },

    currentRoundIndex: {
      get() {
        // Check override first, then element default, finally 0
        return (
          this.elementFields?.currentRoundIndex ??
          this.element.currentRoundIndex ??
          0
        );
      },

      set(value) {
        this.updateField("currentRoundIndex", value);
      },
    },

    currentDisplayState: {
      get() {
        // Check override first, then element default, finally 'results'
        return (
          this.elementFields?.currentDisplayState ??
          this.element.currentDisplayState ??
          "results"
        );
      },

      set(value) {
        this.updateField("currentDisplayState", value);
      },
    },

    currentQuestionIndex: {
      get() {
        // Check override first, then element default, finally 0
        // Add 1 for display (1-based indexing)
        const idx =
          this.elementFields?.currentQuestionIndex ??
          this.element.currentQuestionIndex ??
          0;
        return idx + 1;
      },

      set(value) {
        // Convert back to 0-based index
        const idx = value - 1;
        this.updateField("currentQuestionIndex", idx);
      },
    },

    animateResults: {
      get() {
        return (
          this.elementFields?.animateResults ??
          this.element.animateResults ??
          false
        );
      },

      set(value) {
        this.updateField("animateResults", value);
      },
    },
  },

  async mounted() {
    // Load poll data if element has pollId
    if (this.pollId) {
      await this.loadPollData();
    }
  },

  watch: {
    // Watch pollId changes to reload poll data
    pollId: {
      immediate: true,
      async handler(newPollId, oldPollId) {
        if (newPollId && newPollId !== oldPollId) {
          await this.loadPollData();
        }
      },
    },
  },

  methods: {
    ...mapActions("graphicEditor", ["updateGraphicField"]),

    async loadPollData() {
      if (!this.pollId) {
        console.warn("No pollId available to load poll data");
        return;
      }

      if (this.isLoadingPoll) {
        return;
      }

      try {
        this.isLoadingPoll = true;

        // Use the polls service which wraps fetch with proper error handling
        this.pollData = await getPoll(this.pollId);
      } catch (error) {
        console.error("Error loading poll data:", error);
        this.pollData = null;
      } finally {
        this.isLoadingPoll = false;
      }
    },

    updateField(field, value) {
      this.$emit("value", { field, value });
    },
  },
};
</script>
